import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import "./navbar.css";
import PositionBar from "../position-bar/position-bar";
import { useLanguage } from "../../i18n/LanguageContext";

import LogoWhite from "../../assets/metagent-logo.webp";

const Navbar = () => {
  const navRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { lang, setLang, tr } = useLanguage();

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [navbarVisible, setNavbarVisible] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const lastScrollYRef = useRef(0);
  const hasUserScrolledRef = useRef(false);
  const location = useLocation();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isMobile) {
      // Mobile: don't use GSAP
      return;
    }

    // Desktop: No GSAP animation needed for horizontal navbar
    // Menu is always visible
    if (menuRef.current) {
      menuRef.current.style.opacity = "1";
      menuRef.current.style.transform = "translateY(0)";
      menuRef.current.style.pointerEvents = "auto";
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) {
      // Mobile: handle sidebar outside click
      const handleClickOutside = (e) => {
        const sidebar = document.querySelector('.navbar-sidebar');
        const hamburger = document.querySelector('.navbar-hamburger');
        if (sidebarOpen && sidebar && !sidebar.contains(e.target) && !hamburger?.contains(e.target)) {
          setSidebarOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isMobile, sidebarOpen]);

  // Navbar hilang saat scroll ke bawah, muncul lagi saat scroll ke atas / di top
  useEffect(() => {
    if (isMobile) return;

    lastScrollYRef.current = window.scrollY;
    setNavbarVisible(true);
    setIsScrolled(window.scrollY > 50);

    let ticking = false;
    const SCROLL_TOP_THRESHOLD = 80;
    const SCROLL_HIDE_THRESHOLD = 100;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollY = window.scrollY || window.pageYOffset;
        const lastScrollY = lastScrollYRef.current;
        const isLanding = location.pathname === '/';
        const heroFirstPhaseHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
        const inHeroFirstPhase = isLanding && scrollY < heroFirstPhaseHeight;

        if (scrollY !== lastScrollY) {
          hasUserScrolledRef.current = true;
        }

        if (!hasUserScrolledRef.current) {
          setNavbarVisible(true);
          lastScrollYRef.current = scrollY;
          ticking = false;
          return;
        }

        const ALWAYS_SHOW_TOP = SCROLL_TOP_THRESHOLD;
        if (scrollY <= ALWAYS_SHOW_TOP) {
          setNavbarVisible(true);
          lastScrollYRef.current = scrollY;
          ticking = false;
          return;
        }

        setIsScrolled(scrollY > 50);

        if (inHeroFirstPhase) {
          setNavbarVisible(true);
        } else if (scrollY <= SCROLL_TOP_THRESHOLD) {
          setNavbarVisible(true);
        } else if (scrollY > lastScrollY) {
          if (scrollY > SCROLL_HIDE_THRESHOLD) setNavbarVisible(false);
        } else {
          setNavbarVisible(true);
        }

        lastScrollYRef.current = scrollY;
        ticking = false;
      });
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMobile, location.pathname]);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  // Mobile: Hamburger menu with sidebar
  if (isMobile) {
    return (
      <>
        {/* Hamburger Button */}
        <button className="navbar-hamburger" onClick={toggleSidebar}>
          <img
            src={LogoWhite}
            alt="Metagent Studio"
            className="navbar-hamburger-logo"
          />
        </button>

        {/* Sidebar Overlay */}
        <AnimatePresence>
          {sidebarOpen && (
            <>
              <motion.div
                className="navbar-sidebar-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                onClick={toggleSidebar}
              />
              <motion.div
                className="navbar-sidebar"
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
              >
                <div className="navbar-sidebar-header">
                  <img
                    src={LogoWhite}
                    alt="Metagent Studio"
                    className="navbar-sidebar-logo"
                  />
                  <button
                    className="navbar-sidebar-close"
                    onClick={toggleSidebar}
                  >
                    ×
                  </button>
                </div>
                <nav className="navbar-sidebar-menu">
                  <Link to="/" onClick={toggleSidebar}>{tr("Home", "Beranda")}</Link>
                  <Link to="/portfolio" onClick={toggleSidebar}>{tr("Portfolio", "Portofolio")}</Link>
                  <Link to="/pricing" onClick={toggleSidebar}>{tr("Pricing", "Harga")}</Link>
                  <Link to="/how-we-work" onClick={toggleSidebar}>{tr("How We Work", "Cara Kerja")}</Link>
                  <Link to="/faq" onClick={toggleSidebar}>FAQ</Link>
                  <button
                    className="navbar-lang-toggle"
                    onClick={() => setLang(lang === "en" ? "id" : "en")}
                  >
                    {lang.toUpperCase()}
                  </button>
                  <a
                    href="#contact-us"
                    onClick={(e) => {
                      toggleSidebar();
                      e.preventDefault();
                      const isLandingPage = location.pathname === '/';
                      
                      if (isLandingPage) {
                        // On landing page: scroll to form
                      let formSection = document.getElementById("form");
                      if (!formSection) {
                        const contactUsAnchor = document.getElementById("contact-us");
                        if (contactUsAnchor) {
                          formSection = contactUsAnchor.closest('section');
                        } else {
                          formSection = document.querySelector('[data-contact-us="true"]');
                        }
                      }
                      if (formSection) {
                        const navbarHeight = 72;
                        const elementPosition = formSection.getBoundingClientRect().top + window.pageYOffset;
                        const offsetPosition = elementPosition - navbarHeight;
                        window.scrollTo({
                          top: offsetPosition,
                          behavior: "smooth",
                        });
                        // Focus on first input after scroll
                        setTimeout(() => {
                          const firstInput = formSection.querySelector('input[type="text"]');
                          if (firstInput) {
                            firstInput.focus();
                          }
                        }, 500);
                        }
                      } else {
                        // On other pages: navigate to landing page with hash
                        navigate('/');
                        // Wait for landing page to mount, then scroll to form
                        setTimeout(() => {
                          window.location.hash = '#contact-us';
                          const scrollToForm = () => {
                            const formSection = document.getElementById("form") || document.querySelector('[data-contact-us="true"]');
                            if (formSection) {
                              const navbarHeight = 72;
                              const elementPosition = formSection.getBoundingClientRect().top + window.pageYOffset;
                              const offsetPosition = elementPosition - navbarHeight;
                              window.scrollTo({
                                top: offsetPosition,
                                behavior: "smooth",
                              });
                              // Focus on first input after scroll
                              setTimeout(() => {
                                const firstInput = formSection.querySelector('input[type="text"]');
                                if (firstInput) {
                                  firstInput.focus();
                                }
                              }, 500);
                            } else {
                              // Retry if form not found yet
                              setTimeout(scrollToForm, 100);
                            }
                          };
                          scrollToForm();
                        }, 300);
                      }
                    }} 
                    className="navbar-sidebar-contact-button"
                  >
                    {tr("Talk to Us", "Hubungi Kami")}
                  </a>
                </nav>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </>
    );
  }

  const isLandingPage = location.pathname === '/';
  const isNavbarHidden = !navbarVisible;

  return (
    <>
      <div ref={navRef} className={`navbar-glass ${isScrolled ? 'navbar-scrolled' : 'navbar-transparent'} ${isNavbarHidden ? 'navbar-hidden' : ''}`}>
        {/* BLUR LAYER */}
        <div className="navbar-blur" />

        {/* HEADER - Logo+Menu (grup kiri), Button */}
        <div className="navbar-header">
          <div className="navbar-left-group">
            <Link to="/" className="navbar-logo-wrapper">
              <img
                src={LogoWhite}
                alt="Metagent Studio"
                className="navbar-logo"
              />
            </Link>
            <div ref={menuRef} className="navbar-menu">
              <Link to="/">{tr("Home", "Beranda")}</Link>
              <Link to="/portfolio">{tr("Portfolio", "Portofolio")}</Link>
              <Link to="/pricing">{tr("Pricing", "Harga")}</Link>
              <Link to="/how-we-work">{tr("How We Work", "Cara Kerja")}</Link>
              <Link to="/faq">FAQ</Link>
            </div>
          </div>
          {/* BUTTONS */}
          <div className="navbar-actions">
            <button
              className="navbar-lang-toggle"
              onClick={() => setLang(lang === "en" ? "id" : "en")}
            >
              {lang.toUpperCase()}
            </button>
            <a 
              href="#contact-us" 
              className="navbar-contact-button"
              onClick={(e) => {
              // Prevent default anchor behavior
              e.preventDefault();
              const isLandingPage = location.pathname === '/';
              
              if (isLandingPage) {
                // On landing page: scroll to form
              let formSection = document.getElementById("form");
              if (!formSection) {
                const contactUsAnchor = document.getElementById("contact-us");
                if (contactUsAnchor) {
                  formSection = contactUsAnchor.closest('section');
                } else {
                  formSection = document.querySelector('[data-contact-us="true"]');
                }
              }
              if (formSection) {
                const navbarHeight = 72;
                const elementPosition = formSection.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - navbarHeight;
                window.scrollTo({
                  top: offsetPosition,
                  behavior: "smooth",
                });
                // Focus on first input after scroll
                setTimeout(() => {
                  const firstInput = formSection.querySelector('input[type="text"]');
                  if (firstInput) {
                    firstInput.focus();
                  }
                }, 500);
                }
              } else {
                // On other pages: navigate to landing page with hash
                navigate('/');
                // Wait for landing page to mount, then scroll to form
                setTimeout(() => {
                  window.location.hash = '#contact-us';
                  const scrollToForm = () => {
                    const formSection = document.getElementById("form") || document.querySelector('[data-contact-us="true"]');
                    if (formSection) {
                      const navbarHeight = 72;
                      const elementPosition = formSection.getBoundingClientRect().top + window.pageYOffset;
                      const offsetPosition = elementPosition - navbarHeight;
                      window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth",
                      });
                      // Focus on first input after scroll
                      setTimeout(() => {
                        const firstInput = formSection.querySelector('input[type="text"]');
                        if (firstInput) {
                          firstInput.focus();
                        }
                      }, 500);
                    } else {
                      // Retry if form not found yet
                      setTimeout(scrollToForm, 100);
                    }
                  };
                  scrollToForm();
                }, 300);
              }
              }}
            >
              {tr("Talk to Us", "Hubungi Kami")}
            </a>
            <Link
              to="/ai-diagnostic"
              className="navbar-ai-diagnostic-button"
            >
              {tr("Free AI Diagnostic", "Free AI Diagnostic")}
            </Link>
          </div>
        </div>
      </div>
      {/* Position Bar - Only show on landing page and desktop */}
      {isLandingPage && !isMobile && <PositionBar />}
    </>
  );
};

export default Navbar;
